import _ from 'lodash';

import {
  mapResponseToJson,
  mapResponseToText,
  UnsucessfulFetchError
} from '@atlassiansox/asc-core';
import { generateAsapHeader } from '@atlassiansox/asc-asap';
import { fetch, Headers } from 'cross-fetch';

import {
  CHECK_ADD_PROJECT_ACTION,
  CHECK_ADD_PROJECT_ARI_PREFIX,
  CheckAddProductCapabilitiesRequest,
  CheckAddProductCapabilitiesRequestItem,
  CheckAddProductCapabilitiesResponse,
  CheckAuthenticationMethodsAvailableRequest,
  CheckAuthenticationMethodsAvailableResponse,
  isAsapAuth,
  PermissionsPermittedRequest,
  PermissionsPermittedRequestParams,
  PermissionsPermittedResponse,
  PermissionsBulkPermittedRequest,
  GetUserUpstreamResponse,
  SignupRequest,
  SignupRequestOptions,
  UserSignupResponse,
  GetUsersByCloudIdRequest,
  GetUsersByCloudIdResponse,
  GetUserByCloudAndAaIdRequest,
  GetUserByCloudAndAaIdResponse,
  GetPrincipalsByResourceRequest,
  GetPrincipalsByResourceResponse,
  PermissionsBulkPermittedResponse,
  InviteUserRequest,
  InviteUserResponse,
  GetUserIdByEmailAddressesRequest,
  GetUserIdByEmailAddressesResponseItem
} from './identity-client.types';

export const DEFAULT_IDENTITY_BASE_URL = 'https://id.staging.atl-paas.net';
const ASAP_AUDIENCE = 'identity-platform';

export interface IIdentityClient {
  permissionsPermitted(
    request: PermissionsPermittedRequest
  ): Promise<PermissionsPermittedResponse>;
  permissionsBulkPermitted(
    request: PermissionsBulkPermittedRequest
  ): Promise<PermissionsBulkPermittedResponse>;
  checkAddProductCapabilities(
    request: CheckAddProductCapabilitiesRequest
  ): Promise<CheckAddProductCapabilitiesResponse>;
  checkAuthenticationMethodsAvailable(
    options: CheckAuthenticationMethodsAvailableRequest
  ): Promise<CheckAuthenticationMethodsAvailableResponse>;
  getUser(accountId: string): Promise<GetUserUpstreamResponse | undefined>;
  getUserName(accountId: string): Promise<string | undefined>;
  userSignup(
    request: SignupRequest,
    options: SignupRequestOptions
  ): Promise<UserSignupResponse>;
  getUsersByCloudId(
    request: GetUsersByCloudIdRequest
  ): Promise<GetUsersByCloudIdResponse>;
  getHealthCheck(): Promise<string | undefined>;
  getUserByCloudAndAaId(
    request: GetUserByCloudAndAaIdRequest
  ): Promise<GetUserByCloudAndAaIdResponse>;
  getPrincipalsByResource(
    request: GetPrincipalsByResourceRequest
  ): Promise<GetPrincipalsByResourceResponse>;
  inviteUser(request: InviteUserRequest): Promise<InviteUserResponse>;
  getUserIdsByEmailAddresses(
    emailAddresses: GetUserIdByEmailAddressesRequest
  ): Promise<GetUserIdByEmailAddressesResponseItem[]>;
}

export type IdentityClientOptions = {
  readonly baseUrlStargate?: string;
  readonly baseUrlIdentity?: string;
};

export class IdentityClient implements IIdentityClient {
  private readonly baseUrlIdentity: string;

  public constructor({
    baseUrlIdentity = DEFAULT_IDENTITY_BASE_URL
  }: IdentityClientOptions = {}) {
    this.baseUrlIdentity = baseUrlIdentity;
  }

  public async permissionsPermitted(
    request: PermissionsPermittedRequest
  ): Promise<PermissionsPermittedResponse> {
    const {
      auth,
      permissionId,
      principalId,
      resourceId,
      dontRequirePrincipalInSite
    } = request;

    // URL is defined globally for node versions >= 10.0.0 and globally in browsers,
    // so I'm assuming this should work cross platform
    const url = new URL(`${this.baseUrlIdentity}/v1/permissions/permitted`);
    const params: PermissionsPermittedRequestParams = {
      permissionId,
      principalId,
      resourceId,
      dontRequirePrincipalInSite
    };
    _.map(params, (val, key) => {
      url.searchParams.append(key, val.toString());
    });

    const headers = new Headers();

    if (isAsapAuth(auth)) {
      headers.append(
        'Authorization',
        generateAsapHeader({
          ...auth.asap,
          audience: ASAP_AUDIENCE
        })
      );
    }

    return mapResponseToText(
      await fetch(`${url}`, {
        method: 'GET',
        headers
      })
    );
  }

  public async permissionsBulkPermitted(
    request: PermissionsBulkPermittedRequest
  ): Promise<PermissionsBulkPermittedResponse> {
    const { auth, query } = request;

    const url = new URL(
      `${this.baseUrlIdentity}/v3/permissions/bulk/permitted`
    );

    const headers = new Headers();

    if (isAsapAuth(auth)) {
      headers.append(
        'Authorization',
        generateAsapHeader({
          ...auth.asap,
          audience: ASAP_AUDIENCE
        })
      );
    }

    headers.append('Accept', 'application/json');
    headers.append('Content-Type', 'application/json');

    return mapResponseToText(
      await fetch(`${url}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(query)
      })
    );
  }

  public async checkAddProductCapabilities(
    request: CheckAddProductCapabilitiesRequest
  ): Promise<CheckAddProductCapabilitiesResponse> {
    const { auth, cloudId, product, licenceLimit } = request;

    const url = new URL(`${this.baseUrlIdentity}/v2/licences/capabilities`);
    const requestData: CheckAddProductCapabilitiesRequestItem[] = [
      {
        action: CHECK_ADD_PROJECT_ACTION,
        owner: `${CHECK_ADD_PROJECT_ARI_PREFIX}${cloudId}`,
        product,
        licenceLimit
      }
    ];
    const headers = new Headers();

    headers.append('Content-Type', 'application/json');

    if (isAsapAuth(auth)) {
      headers.append(
        'Authorization',
        generateAsapHeader({
          ...auth.asap,
          audience: ASAP_AUDIENCE
        })
      );
    }

    const response = await fetch(`${url}`, {
      method: 'post',
      headers,
      body: JSON.stringify(requestData)
    });

    if (response.ok) {
      return response.json();
    } else {
      throw new UnsucessfulFetchError({
        response
      });
    }
  }

  public async checkAuthenticationMethodsAvailable(
    options: CheckAuthenticationMethodsAvailableRequest
  ): Promise<CheckAuthenticationMethodsAvailableResponse> {
    const { email, redirect_to, application, auth } = options;

    return mapResponseToJson(
      await fetch(
        `${this.baseUrlIdentity}/v2/authentication/methods-available`,
        {
          method: 'POST',
          headers: {
            'Authorization': generateAsapHeader({
              ...auth.asap,
              audience: ASAP_AUDIENCE
            }),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email,
            redirect_to,
            application
          })
        }
      )
    );
  }

  public async getUser(
    accountId: string
  ): Promise<GetUserUpstreamResponse | undefined> {
    const result = await fetch(`${this.baseUrlIdentity}/v2/users/${accountId}`);

    if (result.status === 404) {
      return undefined;
    }

    if (!result.ok) {
      throw new Error(await result.text());
    }

    return (await result.json()) as GetUserUpstreamResponse;
  }

  public async getUserName(accountId: string): Promise<string | undefined> {
    const user = await this.getUser(accountId);
    if (!user) {
      return undefined;
    }
    return user.name;
  }

  public async getHealthCheck(): Promise<string | undefined> {
    return mapResponseToText(
      await fetch(`${this.baseUrlIdentity}/healthcheck`)
    );
  }

  public async userSignup(
    request: SignupRequest,
    options: SignupRequestOptions
  ): Promise<UserSignupResponse> {
    const {
      application,
      displayName,
      email,
      locale,
      recaptchaScore,
      redirectTo,
      suppressEmail,
      userAgent,
      anonymousId
    } = request;

    const headers = new Headers({
      'Authorization': generateAsapHeader({
        ...options.auth.asap,
        audience: ASAP_AUDIENCE
      }),
      'Content-Type': 'application/json'
    });

    if (anonymousId) {
      headers.append('X-Atl-Anonymous-Id', anonymousId);
    }

    const context = {
      ...(userAgent && {
        deviceInfo: {
          userAgent
        }
      }),
      ...(recaptchaScore && {
        riskScore: {
          type: 'reCAPTCHA',
          value: recaptchaScore
        }
      })
    };
    const body = {
      application,
      ...(displayName && { displayName }),
      email,
      ...(locale && { locale }),
      ...(suppressEmail !== undefined && { suppressEmail }),
      ...(redirectTo && { redirect_to: redirectTo }),
      ...(Object.keys(context).length && { context })
    };

    return mapResponseToJson(
      await fetch(`${this.baseUrlIdentity}/v2/users/signup`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...body,
          redirect_to: redirectTo
        })
      })
    );
  }

  public async getUsersByCloudId(
    request: GetUsersByCloudIdRequest
  ): Promise<GetUsersByCloudIdResponse> {
    return mapResponseToJson<GetUsersByCloudIdResponse>(
      await fetch(`${this.baseUrlIdentity}/v1/site/${request.cloudId}/Users`, {
        method: 'GET',
        headers: {
          'Authorization': generateAsapHeader({
            ...request.auth.asap,
            audience: ASAP_AUDIENCE
          }),
          'Content-Type': 'application/json'
        }
      })
    );
  }

  public async getUserByCloudAndAaId(
    request: GetUserByCloudAndAaIdRequest
  ): Promise<GetUserByCloudAndAaIdResponse> {
    return mapResponseToJson(
      await fetch(
        `${this.baseUrlIdentity}/v1/site/${request.cloudId}/Users/${request.atlassianAccountId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': generateAsapHeader({
              ...request.auth.asap,
              audience: ASAP_AUDIENCE
            }),
            'Content-Type': 'application/json'
          }
        }
      )
    );
  }

  public async getPrincipalsByResource(
    request: GetPrincipalsByResourceRequest
  ): Promise<GetPrincipalsByResourceResponse> {
    return mapResponseToJson(
      await fetch(
        `${this.baseUrlIdentity}/v3/permissions/principalsByResource`,
        {
          method: 'POST',
          headers: {
            'Authorization': generateAsapHeader({
              ...request.auth.asap,
              audience: ASAP_AUDIENCE
            }),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            principalFilter: request.principalFilter,
            resourceId: request.resourceId,
            permissionId: request.permissionId
          })
        }
      )
    );
  }

  public async inviteUser(
    request: InviteUserRequest
  ): Promise<InviteUserResponse> {
    const { auth, ...requestPayload } = request;

    const response = await fetch(`${this.baseUrlIdentity}/v1/Users/invite`, {
      method: 'Post',
      headers: {
        'Authorization': generateAsapHeader({
          ...auth.asap,
          audience: ASAP_AUDIENCE
        }),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestPayload)
    });

    if (!response.ok) {
      throw new UnsucessfulFetchError({
        message: `${response.statusText} ${await response.text()}`,
        response
      });
    }

    const invitationUrl = response.headers.get('location');
    if (!invitationUrl) {
      throw new Error(
        'invitation request succeeded but no location header was returned'
      );
    }

    return { invitationUrl };
  }

  public async getUserIdsByEmailAddresses(
    request: GetUserIdByEmailAddressesRequest
  ): Promise<GetUserIdByEmailAddressesResponseItem[]> {
    const response = await fetch(
      `${this.baseUrlIdentity}/v2/users/fetch/by-email`,
      {
        method: 'POST',
        headers: {
          'Authorization': generateAsapHeader({
            ...request.auth.asap,
            audience: ASAP_AUDIENCE
          }),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          emails: request.emailAddresses
        })
      }
    );

    if (!response.ok) {
      throw new Error(`${response.statusText} ${await response.text()}`);
    }

    return response.json();
  }
}
